import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import { hasPermission } from '@/types';
import { useNavigate } from 'react-router-dom';
import {
  vehicles as api, employees as employeesApi, companies as companiesApi, gps as gpsApi,
  type GpsDeviceOption,
} from '@/api/endpoints';
import type { Vehicle, VehicleInput, VehicleStatus, VehicleMeta, Employee, Company, OwnershipType } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { nearestBadge } from './KadryPage';

const STATUSES: { id: VehicleStatus; name: string }[] = [
  { id: 'ACTIVE', name: 'Aktywne' },
  { id: 'IN_SERVICE', name: 'W serwisie' },
  { id: 'RETIRED', name: 'Wycofane' },
];

const STATUS_PILL: Record<VehicleStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  IN_SERVICE: 'bg-amber-100 text-amber-700',
  RETIRED: 'bg-ink-100 text-ink-500',
};

export function PojazdyPage() {
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FLEET_MANAGE');
  const navigate = useNavigate();

  const [items, setItems] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | ''>('ACTIVE');
  const [modalItem, setModalItem] = useState<Vehicle | 'new' | null>(null);

  const reload = () => {
    setLoading(true);
    api.list(statusFilter || undefined)
      .then(setItems)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [statusFilter]);

  const overdue = useMemo(() => items.filter(i => i.nearestUrgency === 'OVERDUE').length, [items]);
  const dueSoon = useMemo(() => items.filter(i => i.nearestUrgency === 'DUE_SOON').length, [items]);

  const onDelete = async (e: React.MouseEvent, v: Vehicle) => {
    e.stopPropagation();
    if (!confirm(`Usunąć „${v.label}"? Usunie to też jego przeglądy.`)) return;
    try { await api.remove(v.id); reload(); }
    catch (er) { alert('Błąd: ' + (er instanceof Error ? er.message : String(er))); }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Pojazdy i sprzęt</h1>
          <div className="text-ink-400 text-[13px]">Pojazdy i sprzęt specjalistyczny — badania techniczne, OC/AC, UDT, tachograf, przeglądy</div>
        </div>
        {canEdit && (
          <button onClick={() => setModalItem('new')} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pojazd/sprzęt
          </button>
        )}
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setStatusFilter('')}
            className={'btn btn-sm ' + (statusFilter === '' ? 'btn-primary' : 'btn-ghost')}>Wszystkie</button>
          {STATUSES.map(s => (
            <button key={s.id} onClick={() => setStatusFilter(s.id)}
              className={'btn btn-sm ' + (statusFilter === s.id ? 'btn-primary' : 'btn-ghost')}>{s.name}</button>
          ))}
        </div>
        {(overdue > 0 || dueSoon > 0) && (
          <div className="ml-auto flex items-center gap-2 text-[12px]">
            {overdue > 0 && <span className="pill pill-red">{overdue} po terminie</span>}
            {dueSoon > 0 && <span className="pill" style={{ background: '#fef3c7', color: '#b45309' }}>{dueSoon} wkrótce</span>}
          </div>
        )}
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak pojazdów w tym widoku.{canEdit ? ' Kliknij „Dodaj pojazd/sprzęt".' : ''}
        </div></div>
      )}

      {!loading && items.length > 0 && (
        <div className="card"><div className="card-body !p-0">
          <table className="table-hw">
            <thead>
              <tr>
                <th>Pojazd / sprzęt</th>
                <th className="w-32">Nr rej.</th>
                <th className="w-40">Kierowca/operator</th>
                <th className="w-28">Status</th>
                <th className="w-48">Najbliższy termin</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(v => (
                <tr key={v.id} className="cursor-pointer hover:bg-ink-50/50" onClick={() => navigate(`/pojazdy/${v.id}`)}>
                  <td>
                    <div className="font-semibold text-ink-900">{v.label}</div>
                    <div className="text-[11px] text-ink-400">{v.typeName}{v.brand ? ' · ' + v.brand : ''}{v.model ? ' ' + v.model : ''}</div>
                  </td>
                  <td className="font-mono text-[12px]">{v.registrationNumber || '—'}</td>
                  <td className="text-[12px] text-ink-500">{v.assignedEmployeeName || '—'}</td>
                  <td>
                    <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' + STATUS_PILL[v.status]}>{v.statusName}</span>
                  </td>
                  <td>{nearestBadge(v.nearestDaysUntil, v.nearestUrgency, v.nearestDeadlineLabel)}</td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      {canEdit && <button onClick={ev => { ev.stopPropagation(); setModalItem(v); }} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600" title="Edytuj"><Icon name="edit" className="w-3.5 h-3.5"/></button>}
                      {canEdit && <button onClick={ev => onDelete(ev, v)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Usuń"><Icon name="x" className="w-3.5 h-3.5"/></button>}
                      <Icon name="chevron" className="w-3.5 h-3.5 text-ink-300"/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      {modalItem && (
        <VehicleModal
          item={modalItem === 'new' ? null : modalItem}
          onClose={() => setModalItem(null)}
          onSaved={() => { setModalItem(null); reload(); }}
        />
      )}
    </div>
  );
}

/** Modal tworzenia/edycji pojazdu. Eksportowany — używany też na stronie szczegółów. */
export function VehicleModal({ item, onClose, onSaved }: {
  item: Vehicle | null;
  onClose: () => void;
  onSaved: (saved: Vehicle) => void;
}) {
  const isEdit = !!item;
  const [type, setType] = useState(item?.type ?? 'CAR');
  const [label, setLabel] = useState(item?.label ?? '');
  const [brand, setBrand] = useState(item?.brand ?? '');
  const [model, setModel] = useState(item?.model ?? '');
  const [registrationNumber, setRegistrationNumber] = useState(item?.registrationNumber ?? '');
  const [vin, setVin] = useState(item?.vin ?? '');
  const [productionYear, setProductionYear] = useState<string>(item?.productionYear != null ? String(item.productionYear) : '');
  const [acquisitionDate, setAcquisitionDate] = useState(item?.acquisitionDate ?? '');
  const [odometer, setOdometer] = useState<string>(item?.odometer != null ? String(item.odometer) : '');
  const [status, setStatus] = useState<VehicleStatus>(item?.status ?? 'ACTIVE');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState<number | ''>(item?.assignedEmployeeId ?? '');
  const [companyId, setCompanyId] = useState<number | ''>(item?.companyId ?? '');
  // dane flotowe (V82)
  const [ownershipType, setOwnershipType] = useState<OwnershipType>(item?.ownershipType ?? 'OWNED');
  const [financingCompany, setFinancingCompany] = useState(item?.financingCompany ?? '');
  const [financingContractNo, setFinancingContractNo] = useState(item?.financingContractNo ?? '');
  const [financingMonthlyCost, setFinancingMonthlyCost] = useState<string>(item?.financingMonthlyCost != null ? String(item.financingMonthlyCost) : '');
  const [fuelCardNumber, setFuelCardNumber] = useState(item?.fuelCardNumber ?? '');
  const [etollId, setEtollId] = useState(item?.etollId ?? '');
  const [insurer, setInsurer] = useState(item?.insurer ?? '');
  const [adrValidUntil, setAdrValidUntil] = useState(item?.adrValidUntil ?? '');
  const [gpsDeviceId, setGpsDeviceId] = useState(item?.gpsDeviceId ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  /**
   * Lokalizatory u dostawcy. null = jeszcze nie wiadomo albo integracja nie działa — wtedy pole
   * zostaje zwykłym tekstem, żeby dało się wpisać identyfikator także przy wyłączonej integracji
   * (inaczej wyłączony dostawca blokowałby edycję pola, które jest częścią danych pojazdu).
   */
  const [gpsDevices, setGpsDevices] = useState<GpsDeviceOption[] | null>(null);

  const [meta, setMeta] = useState<VehicleMeta | null>(null);
  const [people, setPeople] = useState<Employee[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    employeesApi.list('ACTIVE').then(setPeople).catch(() => {});
    companiesApi.list().then(setCompanies).catch(() => {});
    // Cicho: brak listy urządzeń (integracja wyłączona) to nie błąd formularza pojazdu.
    gpsApi.devices().then(setGpsDevices).catch(() => setGpsDevices(null));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) { setErr('Nazwa/oznaczenie jest wymagane'); return; }
    setBusy(true); setErr(null);
    const payload: VehicleInput = {
      type: type as VehicleInput['type'], label: label.trim(),
      brand: brand.trim() || null, model: model.trim() || null,
      registrationNumber: registrationNumber.trim() || null, vin: vin.trim() || null,
      productionYear: productionYear === '' ? null : Number(productionYear),
      acquisitionDate: acquisitionDate || null,
      odometer: odometer === '' ? null : Number(odometer),
      status,
      assignedEmployeeId: assignedEmployeeId === '' ? null : Number(assignedEmployeeId),
      companyId: companyId === '' ? null : Number(companyId),
      ownershipType,
      financingCompany: financingCompany.trim() || null,
      financingContractNo: financingContractNo.trim() || null,
      financingMonthlyCost: financingMonthlyCost === '' ? null : Number(financingMonthlyCost),
      fuelCardNumber: fuelCardNumber.trim() || null,
      etollId: etollId.trim() || null,
      insurer: insurer.trim() || null,
      adrValidUntil: adrValidUntil || null,
      gpsDeviceId: gpsDeviceId.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      const saved = isEdit ? await api.update(item!.id, payload) : await api.create(payload);
      onSaved(saved);
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { type, label, brand, model, registrationNumber, vin, productionYear, acquisitionDate, odometer, status, assignedEmployeeId, companyId, ownershipType, financingCompany, financingContractNo, financingMonthlyCost, fuelCardNumber, etollId, insurer, adrValidUntil, gpsDeviceId, notes }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[680px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900">{isEdit ? 'Edytuj pojazd/sprzęt' : 'Nowy pojazd/sprzęt'}</h2>
        </div>
        <div className="p-5 grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Rodzaj *</span>
              <SearchableSelect value={type} onChange={v => setType(v as VehicleInput['type'])}>
                {(meta?.types ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Status</span>
              <SearchableSelect value={status} onChange={v => setStatus(v as VehicleStatus)}>
                {(meta?.statuses ?? STATUSES.map(s => ({ ...s, color: '' }))).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SearchableSelect></label>
          </div>
          <label className="field"><span className="field-label">Nazwa / oznaczenie *</span>
            <input value={label} onChange={e => setLabel(e.target.value)} className="input"
                   placeholder="np. Ciężarówka z HDS / Wiertnica H8 / Renault Master"/></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Marka</span>
              <input value={brand} onChange={e => setBrand(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Model</span>
              <input value={model} onChange={e => setModel(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Rok produkcji</span>
              <input type="number" value={productionYear} onChange={e => setProductionYear(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Nr rejestracyjny</span>
              <input value={registrationNumber} onChange={e => setRegistrationNumber(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">VIN</span>
              <input value={vin} onChange={e => setVin(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Przebieg / mth</span>
              <input type="number" value={odometer} onChange={e => setOdometer(e.target.value)} className="input"/></label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Data nabycia</span>
              <input type="date" value={acquisitionDate} onChange={e => setAcquisitionDate(e.target.value)} className="input"/></label>
            <label className="field"><span className="field-label">Kierowca/operator</span>
              <SearchableSelect value={assignedEmployeeId === '' ? '' : String(assignedEmployeeId)} onChange={v => setAssignedEmployeeId(v ? Number(v) : '')}>
                <option value="">— brak —</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Firma</span>
              <SearchableSelect value={companyId === '' ? '' : String(companyId)} onChange={v => setCompanyId(v ? Number(v) : '')}>
                <option value="">— brak —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </SearchableSelect></label>
          </div>
          <div className="border-t border-ink-100 pt-3 mt-1">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">Dane flotowe</div>
            <div className="grid grid-cols-3 gap-3">
              <label className="field"><span className="field-label">Forma władania</span>
                <SearchableSelect value={ownershipType} onChange={v => setOwnershipType(v as OwnershipType)}>
                  {(meta?.ownershipTypes ?? [
                    { id: 'OWNED', name: 'Własność' }, { id: 'LEASING', name: 'Leasing' },
                    { id: 'RENTAL', name: 'Wynajem / najem' }, { id: 'LOAN', name: 'Użyczenie' },
                  ]).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </SearchableSelect></label>
              <label className="field"><span className="field-label">Ubezpieczyciel</span>
                <input value={insurer} onChange={e => setInsurer(e.target.value)} className="input" placeholder="np. PZU, Warta"/></label>
              <label className="field"><span className="field-label">Ważność ADR</span>
                <input type="date" value={adrValidUntil} onChange={e => setAdrValidUntil(e.target.value)} className="input"/></label>
            </div>
            {ownershipType !== 'OWNED' && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                <label className="field"><span className="field-label">{ownershipType === 'RENTAL' ? 'Wynajmujący' : 'Finansujący / leasingodawca'}</span>
                  <input value={financingCompany} onChange={e => setFinancingCompany(e.target.value)} className="input"/></label>
                <label className="field"><span className="field-label">Nr umowy</span>
                  <input value={financingContractNo} onChange={e => setFinancingContractNo(e.target.value)} className="input"/></label>
                <label className="field"><span className="field-label">Rata / czynsz mies. (PLN)</span>
                  <input type="number" min="0" step="0.01" value={financingMonthlyCost} onChange={e => setFinancingMonthlyCost(e.target.value)} className="input"/></label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="field"><span className="field-label">Nr karty paliwowej (DKV)</span>
                <input value={fuelCardNumber} onChange={e => setFuelCardNumber(e.target.value)} className="input"/></label>
              <label className="field"><span className="field-label">E-toll ID</span>
                <input value={etollId} onChange={e => setEtollId(e.target.value)} className="input" placeholder="np. Z21-AZ94AW-0"/></label>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="field">
                <span className="field-label">Urządzenie GPS</span>
                {gpsDevices && gpsDevices.length > 0 ? (
                  <SearchableSelect value={gpsDeviceId} onChange={v => setGpsDeviceId(v)}>
                    <option value="">— dopasuj automatycznie po nr rejestracyjnym —</option>
                    {gpsDevices.map(d => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {(d.deviceName ?? d.deviceId)
                          + (d.assignedVehicleId != null && d.assignedVehicleId !== item?.id
                            ? ` — zajęte: ${d.assignedVehicleLabel ?? '?'}` : '')}
                      </option>
                    ))}
                    {/* Wskazanie zapisane wcześniej, którego nie ma już na liście dostawcy — bez tej
                        pozycji zapis formularza po cichu skasowałby powiązanie. */}
                    {gpsDeviceId && !gpsDevices.some(d => d.deviceId === gpsDeviceId) && (
                      <option value={gpsDeviceId}>{gpsDeviceId} — urządzenia nie ma już u dostawcy</option>
                    )}
                  </SearchableSelect>
                ) : (
                  <input value={gpsDeviceId} onChange={e => setGpsDeviceId(e.target.value)} className="input"
                         placeholder="ID urządzenia u dostawcy (puste = dopasuj po nr rejestracyjnym)"/>
                )}
              </label>
            </div>
            <div className="text-[11px] text-ink-400 mt-1">
              Puste pole wystarcza, gdy dostawca nazywa lokalizator numerem rejestracyjnym pojazdu —
              wtedy powiązanie powstaje samo. Wskazanie ręczne przydaje się po przełożeniu lokalizatora
              do innego auta albo po przerejestrowaniu pojazdu.
            </div>
          </div>

          <label className="field"><span className="field-label">Notatki</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz' : 'Dodaj')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
